'use client'

import { cn } from '@/lib/utils'
import Image from 'next/image'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface VideoBannerProps {
  videoSrc: string
  fallbackImageSrc?: string
  alt?: string
  className?: string
  priority?: boolean
  children?: React.ReactNode
}

export default function VideoBanner({
  videoSrc,
  fallbackImageSrc,
  alt = 'Banner',
  className,
  priority = false,
  children
}: VideoBannerProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [shouldLoad, setShouldLoad] = useState(priority)
  const [videoReady, setVideoReady] = useState(false)
  const [videoError, setVideoError] = useState(false)

  const onInView = useCallback(([entry]: IntersectionObserverEntry[]) => {
    if (entry?.isIntersecting) setShouldLoad(true)
  }, [])

  useEffect(() => {
    if (priority) return
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(onInView, {
      rootMargin: '80px',
      threshold: 0.01
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [priority, onInView])

  const handleReady = useCallback(() => {
    setVideoReady(true)
    videoRef.current?.play().catch(() => {})
  }, [])

  useEffect(() => {
    if (!shouldLoad || !videoRef.current) return
    const video = videoRef.current
    const tryPlay = () => {
      if (video.readyState >= 2) {
        setVideoReady(true)
        video.play().catch(() => {})
      }
    }
    video.addEventListener('loadeddata', tryPlay, { once: true })
    video.addEventListener('canplay', tryPlay, { once: true })
    const t = setTimeout(tryPlay, 500)
    return () => {
      video.removeEventListener('loadeddata', tryPlay)
      video.removeEventListener('canplay', tryPlay)
      clearTimeout(t)
    }
  }, [shouldLoad])

  return (
    <div
      ref={containerRef}
      className={cn('relative h-full w-full overflow-hidden bg-black', className)}
    >
      {fallbackImageSrc && (
        <Image
          src={fallbackImageSrc}
          alt={alt}
          fill
          className={cn(
            'object-cover',
            shouldLoad && videoReady && !videoError && 'pointer-events-none'
          )}
          priority={priority}
          sizes='100vw'
        />
      )}
      {shouldLoad && (
        <video
          ref={videoRef}
          className={cn(
            'absolute inset-0 z-[1] h-full w-full object-cover',
            'transition-opacity duration-500 ease-out',
            '[transform:translateZ(0)]',
            videoReady && !videoError ? 'opacity-100' : 'opacity-0',
            videoError && 'hidden'
          )}
          autoPlay
          loop
          muted
          playsInline
          preload='auto'
          src={videoSrc}
          onCanPlay={handleReady}
          onLoadedData={handleReady}
          onError={() => setVideoError(true)}
        />
      )}
      {children ?
        <div className='pointer-events-none absolute inset-0 z-10 [&>*]:pointer-events-auto'>
          {children}
        </div>
      : null}
    </div>
  )
}
